'use es6';

import { ACTION_HUB_OPT_IN, ACTION_HUB_OPT_OUT, ACTION_USER_OPT_OUT } from 'ui-addon-opt/state/OptActions';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { getFeedbackLoader, showFeedbackSurvey } from 'ui-addon-opt/lib/Feedback';
import { trackFeedbackSurveyClick } from 'ui-addon-opt/lib/Tracking';
import { useFeedbackSurveyAvailabilityEffect } from 'ui-addon-opt/effects/OptEffects';
import { BetaButton } from './BetaButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import emptyFunction from 'react-utils/emptyFunction';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultOptOutButtonText = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "ui-addon-opt.optout.label"
});
const Optout = ({
  additionalOptions,
  dispatch,
  feedbackSurveyId,
  learnMore,
  onLearnMoreClick = emptyFunction,
  onMinimized = emptyFunction,
  onView = emptyFunction,
  optinName,
  optOutButtonText = defaultOptOutButtonText,
  showHubOptin = false,
  showHubOptout = false,
  showUserOptout = true,
  usageTracker
}) => {
  const [feedbackClient, setFeedbackClient] = useState(null);
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const feedbackSurveyAvailable = useFeedbackSurveyAvailabilityEffect(dropdownOpened ? feedbackClient : null, dropdownOpened ? feedbackSurveyId : null);
  const onFeedbackClientLoadCb = useCallback(client => {
    setFeedbackClient(client);
  }, []);
  useEffect(() => {
    if (!dropdownOpened) {
      return;
    }

    // onFeedbackClientLoadCb will not be invoked if the client is already loaded by the app
    // in that case, fall back to the global instance
    if (feedbackSurveyId && !feedbackClient && window.hsFeedback) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFeedbackClient(window.hsFeedback);
    }
  }, [feedbackSurveyId, feedbackClient, dropdownOpened]);
  const onFeedbackSurveyClickCb = useCallback(() => {
    showFeedbackSurvey(feedbackClient, feedbackSurveyId);
    trackFeedbackSurveyClick(usageTracker, optinName);
  }, [feedbackClient, feedbackSurveyId, optinName, usageTracker]);
  const onDropdownOpen = useCallback(() => {
    setDropdownOpened(true);
    onView();
  }, [onView]);
  const FeedbackLoader = useMemo(() => feedbackSurveyId && dropdownOpened ? getFeedbackLoader() : null, [feedbackSurveyId, dropdownOpened]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Dropdown, {
      Button: BetaButton,
      buttonText: optOutButtonText,
      onOpenComplete: onDropdownOpen,
      "data-test-id": "dropdown",
      children: /*#__PURE__*/_jsxs(List, {
        wrapChildren: true,
        children: [showUserOptout && /*#__PURE__*/_jsx(Button, {
          onClick: () => dispatch({
            type: ACTION_USER_OPT_OUT
          }),
          "data-test-id": "userOptoutButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optout.options.userOptout"
          })
        }), showHubOptout && /*#__PURE__*/_jsx(Button, {
          onClick: () => dispatch({
            type: ACTION_HUB_OPT_OUT
          }),
          "data-test-id": "hubOptoutButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optout.options.hubOptout"
          })
        }), showHubOptin && /*#__PURE__*/_jsx(Button, {
          onClick: () => dispatch({
            type: ACTION_HUB_OPT_IN
          }),
          "data-test-id": "hubOptinButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optin.options.hubOptin"
          })
        }), learnMore && /*#__PURE__*/_jsx(Button, {
          href: learnMore.url,
          onClick: onLearnMoreClick,
          target: "_blank",
          "data-test-id": "learnMoreButton",
          children: learnMore.text
        }), feedbackSurveyAvailable && /*#__PURE__*/_jsx(Button, {
          onClick: onFeedbackSurveyClickCb,
          "data-test-id": "submitFeedbackButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.optout.options.submitFeedback"
          })
        }), additionalOptions && additionalOptions.map(({
          text,
          onClick
        }, idx) => /*#__PURE__*/_jsx(Button, {
          onClick: onClick,
          "data-test-id": `additionalOptionsButton${idx}`,
          children: text
        }, `option-${idx}`)), /*#__PURE__*/_jsx(Button, {
          onClick: onMinimized,
          "data-test-id": "minimizeButton",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-addon-opt.common.options.minimize"
          })
        })]
      })
    }), FeedbackLoader &&
    /*#__PURE__*/
    // eslint-disable-next-line react-hooks/static-components
    _jsx(FeedbackLoader, {
      onClientLoad: onFeedbackClientLoadCb
    })]
  });
};

// PropTypes removed for React 19 compatibility. Preserved for future TS migration:
// Optout.propTypes = {
//   additionalOptions: arrayOf(shape({ text: node.isRequired, onClick: func.isRequired })),
//   dispatch: func.isRequired,
//   feedbackSurveyId: string,
//   learnMore: shape({ text: node.isRequired, url: string.isRequired }),
//   onLearnMoreClick: func,
//   onMinimized: func,
//   onView: func,
//   optinName: string.isRequired,
//   optOutButtonText: node,
//   showHubOptin: bool,
//   showHubOptout: bool,
//   showUserOptout: bool,
//   usageTracker: shape({
//     standard: shape({ clone: func.isRequired, track: func.isRequired }),
//     immediate: shape({ clone: func.isRequired, track: func.isRequired }),
//   }),
// };

export default Optout;